/**
 * KloChatService - Handles communication with the Klo chat backend
 * Used for conversational invoice creation and other AI-powered features
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class KloChatService {
  /**
   * Send a message to the Klo chat assistant (non-streaming)
   * @param {string} message - The user's message
   * @param {string} userId - The current user's ID
   * @param {string} conversationId - Optional conversation ID to continue a conversation
   * @returns {Promise<{conversationId: string, message: string, toolResults: Array}>}
   */
  static async sendMessage(message, userId, conversationId = null) {
    try {
      const response = await fetch(`${API_URL}/klo/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          user_id: userId,
          conversation_id: conversationId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }

      const data = await response.json();
      return {
        conversationId: data.conversation_id,
        message: data.message,
        toolResults: data.tool_results || [],
      };
    } catch (error) {
      console.error('KloChatService.sendMessage error:', error);
      throw error;
    }
  }

  /**
   * Send a message with streaming response
   * @param {string} message - The user's message
   * @param {string} userId - The current user's ID
   * @param {string} conversationId - Optional conversation ID
   * @param {Object} callbacks - Callback functions for streaming events
   * @param {Function} callbacks.onDelta - Called with each text delta
   * @param {Function} callbacks.onToolResult - Called when a tool result is received
   * @param {Function} callbacks.onConversationId - Called with the conversation ID
   * @param {Function} callbacks.onDone - Called when streaming is complete
   * @param {Function} callbacks.onError - Called on error
   * @returns {Promise<void>}
   */
  static async sendMessageStream(message, userId, conversationId = null, callbacks = {}) {
    const { onDelta, onToolResult, onConversationId, onDone, onError } = callbacks;

    try {
      const response = await fetch(`${API_URL}/klo/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          user_id: userId,
          conversation_id: conversationId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.error) {
                onError?.(new Error(data.error));
                return;
              }

              if (data.conversation_id) {
                onConversationId?.(data.conversation_id);
              }

              if (data.tool_result) {
                onToolResult?.(data.tool_result);
              }

              if (data.delta) {
                onDelta?.(data.delta);
              }

              if (data.done) {
                onDone?.();
                return;
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete data
            }
          }
        }
      }

      onDone?.();
    } catch (error) {
      console.error('KloChatService.sendMessageStream error:', error);
      onError?.(error);
    }
  }

  /**
   * Clear a conversation
   * @param {string} conversationId - The conversation ID to clear
   * @param {string} userId - The current user's ID
   * @returns {Promise<{success: boolean}>}
   */
  static async clearConversation(conversationId, userId) {
    try {
      const response = await fetch(
        `${API_URL}/klo/chat/${conversationId}?user_id=${userId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to clear conversation');
      }

      return await response.json();
    } catch (error) {
      console.error('KloChatService.clearConversation error:', error);
      throw error;
    }
  }
}

export default KloChatService;
